import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { api } from '../lib/api.js';
import { useRequireAuth } from '../lib/auth.js';
import { Button, Card, Logo, MonoLabel } from '../components/primitives/index.js';
import { ChanceHeroCard, MotiveCard } from '../components/composites/index.js';

export function ContestResult({ id }: { id: string }) {
  const me = useRequireAuth();
  const [, setLocation] = useLocation();
  const generate = useMutation({
    mutationFn: () => api.generatePlangere(id),
    onSuccess: () => setLocation(`/contest/${id}/preview`),
  });

  const contest = useQuery({
    queryKey: ['contest', id],
    queryFn: () => api.contest(id),
    enabled: !!me.data,
  });

  if (me.isLoading || contest.isLoading) {
    return (
      <Shell>
        <div className="text-muted text-sm py-12 text-center">Se încarcă…</div>
      </Shell>
    );
  }

  const c = contest.data?.contest;
  if (!c || !c.analysis_json) {
    return (
      <Shell>
        <Card className="max-w-md mx-auto text-center">
          <h1 className="text-xl font-medium mb-2">Analiză indisponibilă</h1>
          <p className="text-muted text-sm mb-6">
            Analiza juridică nu s-a încheiat încă sau a eșuat. Reia procesul.
          </p>
          <Button
            variant="ghost"
            pill
            icon={false}
            onClick={() => setLocation(`/contest/${id}/questions`)}
          >
            ← La întrebări
          </Button>
        </Card>
      </Shell>
    );
  }

  const a = c.analysis_json;
  const motiveOrdered = [...a.motive].sort((x, y) => x.ordine - y.ordine);

  return (
    <Shell>
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <MonoLabel>Pasul 4 · rezultat analiză</MonoLabel>
        </div>

        <ChanceHeroCard
          percent={a.sanse_succes}
          rezumatCaz={a.rezumat_caz}
          motiveCount={a.motive.length}
        />

        {a.avertismente.length > 0 && (
          <Card inset className="border-warn/40 bg-warn-soft">
            <div className="flex items-start gap-3">
              <span className="text-xl">⚠️</span>
              <div className="flex-1">
                <MonoLabel color="#5C4A0E">Avertismente importante</MonoLabel>
                <ul className="mt-2 space-y-1.5 text-sm text-warn-text leading-relaxed">
                  {a.avertismente.map((w, i) => (
                    <li key={i}>• {w}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        )}

        {motiveOrdered.length > 0 ? (
          <div>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-2xl font-medium">Motivele de nulitate identificate</h2>
              <span className="text-sm text-muted">
                ordonate după putere
              </span>
            </div>
            <div className="grid gap-4">
              {motiveOrdered.map((m) => (
                <MotiveCard key={`${m.ordine}-${m.titlu}`} motive={m} />
              ))}
            </div>
          </div>
        ) : (
          <Card inset className="text-center">
            <h2 className="text-xl font-medium mb-2">Nu am găsit motive solide de nulitate</h2>
            <p className="text-muted text-sm leading-relaxed max-w-md mx-auto">
              Pe baza datelor extrase din PV și a răspunsurilor tale, nu am identificat
              vulnerabilități formale puternice. Asta NU înseamnă că nu poți contesta — doar că
              șansele de a câștiga sunt mai mici. Poți încerca cu cerere de înlocuire amendă cu
              avertisment (art. 7 OG 2/2001).
            </p>
          </Card>
        )}

        <div className="flex flex-wrap gap-3 justify-between items-center pt-4 border-t border-line">
          <Button
            variant="ghost"
            pill
            icon={false}
            onClick={() => setLocation(`/contest/${id}/questions`)}
          >
            ← Modifică răspunsuri
          </Button>
          <div className="flex gap-3 items-center">
            {c.pdf_key ? (
              <Button pill onClick={() => setLocation(`/contest/${id}/preview`)}>
                Vezi plângerea
              </Button>
            ) : (
              <>
                {generate.isError && (
                  <span className="text-xs text-danger">Generare eșuată. Reîncearcă.</span>
                )}
                <Button
                  pill
                  onClick={() => generate.mutate()}
                  disabled={generate.isPending}
                  icon={generate.isPending ? null : '→'}
                >
                  {generate.isPending ? 'Generăm plângerea…' : 'Generează plângerea'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line">
        <Logo />
      </nav>
      <main className="px-4 md:px-6 py-6 md:py-10">{children}</main>
    </div>
  );
}
